import React, { Fragment, useEffect, useRef, useState } from 'react'
import PerfectScrollbar from 'react-perfect-scrollbar'
import PropTypes from 'prop-types'
import { size } from 'lodash'
import clsx from 'clsx'
import { Box, Paper, Tooltip } from '@material-ui/core'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from 'components/Table'

import useStyles from './styles'

const DashboardTable = ({
  columns = [],
  rows = [],
  renderAction = () => {},
  emptyMessage = 'Nenhuma informação encontrada.',
  isLoading = false,
  hasPaper = false,
  noWrap = false,
  children,
  containerProps = {},
  tableHeadProps = {},
}) => {
  const [skeletonRows, setSkeletonRows] = useState(2)

  const classes = useStyles()
  const containerRef = useRef()

  const Container = hasPaper ? Paper : Box

  useEffect(() => {
    if (containerRef.current) {
      const height = containerRef?.current?.offsetHeight
      if (height) setSkeletonRows(Math.ceil(height / 52.5) - 1)
    }
  }, [isLoading])

  return (
    <Fragment>
      <Container className={classes.paper}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          height="100%"
        >
          <Box
            flex={1}
            maxHeight="100%"
            overflow="auto"
            minHeight={0}
            ref={containerRef}
            {...containerProps}
          >
            <PerfectScrollbar
              options={{ wheelPropagation: false, useBothWheelAxes: true }}
              {...containerProps}
            >
              <Table
                emptyMessage={emptyMessage}
                isLoading={isLoading}
                cols={size(columns)}
                stickyHeader
              >
                <TableHead
                  {...tableHeadProps}
                  className={clsx(
                    classes.tableHead,
                    { [classes.tableHeadPaper]: hasPaper },
                    tableHeadProps?.className,
                  )}
                >
                  <TableRow>
                    {columns?.map((col, idx) => (
                      <TableCell
                        key={idx}
                        width={col?.width}
                        align={col?.align || 'left'}
                        className={clsx({
                          [classes.lastTableRow]: idx === size(columns) - 1,
                        })}
                      >
                        {col?.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody skeletonRows={skeletonRows}>
                  {rows?.map((row, rowIdx) => (
                    <TableRow
                      key={rowIdx}
                      className={clsx({
                        [classes.hiddenBorderRow]:
                          !children && rowIdx === size(rows) - 1,
                      })}
                    >
                      {columns.map((col, colIdx) => (
                        <TableCell
                          key={colIdx}
                          width={col?.width}
                          align={col?.align || 'left'}
                          className={clsx({
                            [classes.highlightedRow]: col?.highlight,
                            [classes.lastTableRow]:
                              colIdx === size(columns) - 1,
                            [classes.noWrapContainer]: noWrap,
                          })}
                        >
                          {col.key === 'action' ? (
                            renderAction(row, rowIdx)
                          ) : (
                            <Tooltip title={noWrap ? row[col.key] || '' : ''}>
                              <Box
                                className={clsx({ [classes.noWrap]: noWrap })}
                              >
                                {row[col.key] ?? '-'}
                              </Box>
                            </Tooltip>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </PerfectScrollbar>
          </Box>
          {children && (
            <Box p={1} flexShrink={0}>
              {children}
            </Box>
          )}
        </Box>
      </Container>
    </Fragment>
  )
}

DashboardTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      align: PropTypes.oneOf(['left', 'center', 'right']),
      highlight: PropTypes.bool,
    }),
  ),
  rows: PropTypes.arrayOf(PropTypes.object),
  renderAction: PropTypes.func,
  emptyMessage: PropTypes.string,
  isLoading: PropTypes.bool,
  hasPaper: PropTypes.bool,
  noWrap: PropTypes.bool,
  children: PropTypes.node,
  containerProps: PropTypes.object,
  tableHeadProps: PropTypes.object,
}

export default DashboardTable
